// Renderer de formatos clínicos dinámicos (HC MEDICA). Pinta un formulario a
// partir de un schema { secciones:[{titulo,campos:[{clave,etiqueta,tipo,...}]}] }
// y mantiene las respuestas en un objeto plano {clave: valor} (→ evolucion.datos).
import { AutoTextarea, Campo, Switch, fieldCls, type SchemaFormato } from './shared';

type Valores = Record<string, unknown>;

const anchoCls = (a?: string) => (a === '1/3' ? 'sm:col-span-1' : a === '1/2' ? 'sm:col-span-2' : 'sm:col-span-3');

// IMC automático: si el schema tiene campos peso/talla e imc, lo calcula.
export function calcIMC(peso: unknown, talla: unknown): number | null {
  const p = Number(peso), t = Number(talla);
  if (!p || !t) return null;
  const m = t > 3 ? t / 100 : t;  // acepta cm o m
  return Math.round((p / (m * m)) * 10) / 10;
}

// Lista las etiquetas de los campos `requerido` que están vacíos (para validar antes de firmar).
export function camposRequeridosFaltantes(schema: SchemaFormato, valores: Valores): string[] {
  const faltan: string[] = [];
  for (const sec of schema.secciones ?? []) {
    for (const c of sec.campos) {
      if (c.tipo === 'titulo' || !c.requerido) continue;
      const v = valores[c.clave];
      if (v === undefined || v === null || v === '') faltan.push(c.etiqueta);
    }
  }
  return faltan;
}

export function FormularioClinico({ schema, valores, onChange }: {
  schema: SchemaFormato; valores: Valores; onChange: (v: Valores) => void;
}) {
  // El IMC solo se autocompleta si el formato declara un campo 'imc' (evita
  // escribir una clave fantasma en evolucion.datos para formatos sin IMC).
  const tieneIMC = (schema.secciones ?? []).some((s) => s.campos.some((c) => c.clave === 'imc'));
  const set = (clave: string, valor: unknown) => {
    const next: Valores = { ...valores, [clave]: valor };
    if (tieneIMC && (clave === 'peso' || clave === 'talla')) {
      const imc = calcIMC(clave === 'peso' ? valor : next.peso, clave === 'talla' ? valor : next.talla);
      if (imc != null) next.imc = imc;
    }
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {(schema.secciones ?? []).map((sec, si) => (
        <section key={si} className="space-y-2">
          <p className="text-xs font-semibold uppercase text-muted-foreground border-b pb-1">{sec.titulo}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {sec.campos.map((c) => {
              if (c.tipo === 'titulo') return <p key={c.clave} className="sm:col-span-3 text-sm font-medium">{c.etiqueta}</p>;
              const v = valores[c.clave];
              const lbl = c.etiqueta + (c.unidad ? ` (${c.unidad})` : '') + (c.requerido ? ' *' : '');
              return (
                <div key={c.clave} className={anchoCls(c.ancho)}>
                  {c.tipo === 'checkbox' ? (
                    <Switch checked={!!v} onChange={(b) => set(c.clave, b)} label={lbl} />
                  ) : (
                    <Campo label={lbl}>
                      {c.tipo === 'textarea' ? (
                        <AutoTextarea value={(v as string) ?? ''} onChange={(e) => set(c.clave, e.target.value)} />
                      ) : c.tipo === 'select' ? (
                        <select className={fieldCls} value={(v as string) ?? ''} onChange={(e) => set(c.clave, e.target.value)}>
                          <option value="">— Selecciona —</option>
                          {(c.opciones ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input type={c.tipo === 'numero' ? 'number' : c.tipo === 'fecha' ? 'date' : 'text'}
                          className={fieldCls} value={(v as string | number) ?? ''}
                          readOnly={c.clave === 'imc'}
                          onChange={(e) => set(c.clave, c.tipo === 'numero' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} />
                      )}
                    </Campo>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

// Vista de solo-lectura para impresión / historial: sección → campo: valor.
export function FormularioClinicoVista({ schema, valores }: { schema: SchemaFormato; valores: Valores }) {
  return (
    <div className="space-y-3">
      {(schema.secciones ?? []).map((sec, si) => (
        <div key={si}>
          <p className="text-xs font-semibold uppercase text-muted-foreground">{sec.titulo}</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
            {sec.campos.filter((c) => c.tipo !== 'titulo').map((c) => {
              const v = valores[c.clave];
              if (v === undefined || v === '' || v === null || v === false) return null;
              const txt = c.tipo === 'checkbox' ? 'Sí' : `${v}${c.unidad ? ' ' + c.unidad : ''}`;
              return <div key={c.clave} className="flex gap-1"><dt className="text-muted-foreground">{c.etiqueta}:</dt><dd>{txt}</dd></div>;
            })}
          </dl>
        </div>
      ))}
    </div>
  );
}
